import { useRef } from "react";
import { Utensils, Leaf, Heart, Sun } from "lucide-react";
import useReveal from "@/hooks/useReveal";

const features = [
  { icon: Utensils, label: "Authentische Rezepte" },
  { icon: Leaf, label: "Frische Zutaten" },
  { icon: Heart, label: "Seit 44 Jahren in Höntrop" },
  { icon: Sun, label: "Terrasse im Sommer" },
];

export default function About() {
  const ref = useRef(null);
  useReveal(ref);

  return (
    <section
      id="ueber-uns"
      ref={ref}
      data-testid="about-section"
      className="relative bg-[var(--niko-cream)] py-24 md:py-32 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        {/* Image side */}
        <div className="reveal lg:col-span-6 relative">
          <div className="relative rounded-[2px] overflow-hidden">
            <img
              src="/images/site/innenbereich.webp"
              alt="Gastraum bei Niko's Grill in Bochum-Höntrop"
              className="w-full h-[420px] sm:h-[520px] md:h-[640px] object-cover"
              loading="lazy"
            />
          </div>
          {/* floating accent card */}
          <div className="absolute -bottom-8 -right-4 md:-right-12 bg-[var(--niko-dark)] text-white px-8 py-6 max-w-[260px] shadow-2xl">
            <p className="font-greek italic text-[var(--niko-gold)] text-lg leading-tight">
              "Φιλοξενία"
            </p>
            <p className="text-xs text-white/70 mt-2 leading-relaxed">
              Griechische Gastfreundschaft, seit Jahren in Höntrop.
            </p>
          </div>
          {/* decorative gold bar */}
          <div className="hidden md:block absolute -top-6 -left-6 w-24 h-24 border-t-2 border-l-2 border-[var(--niko-gold)]" />
        </div>

        {/* Text side */}
        <div className="lg:col-span-6">
          <p className="reveal divider-leaf max-w-[180px] mb-8">Über uns</p>
          <h2
            className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--niko-charcoal)] leading-[1.05]"
            data-testid="about-headline"
          >
            Willkommen <br />
            bei{" "}
            <span className="italic text-[var(--niko-blue)]">Niko's</span>.
          </h2>

          <p className="reveal reveal-delay-2 mt-8 text-base md:text-lg text-[var(--niko-charcoal)]/75 leading-[1.85] max-w-xl">
            Niko's Grill ist mehr als ein Restaurant — es ist der Treffpunkt
            von Höntrop. Seit 44 Jahren servieren wir unseren Gästen
            authentische griechische Küche: saftiges Gyros, knusprige Souvlaki,
            cremige Metaxa-Sauce und Vorspeisen wie in Athen.
          </p>
          <p className="reveal reveal-delay-3 mt-5 text-base md:text-lg text-[var(--niko-charcoal)]/75 leading-[1.85] max-w-xl">
            Große Portionen, faire Preise, herzlicher Service —{" "}
            <span className="text-[var(--niko-blue)] font-medium">
              das ist unser Versprechen.
            </span>
          </p>

          <div className="reveal reveal-delay-4 mt-10 grid grid-cols-2 gap-5 max-w-xl">
            {features.map((f, i) => (
              <div
                key={i}
                data-testid={`about-feature-${i}`}
                className="flex items-start gap-3 group"
              >
                <div className="w-10 h-10 shrink-0 rounded-full bg-[var(--niko-blue)]/8 flex items-center justify-center text-[var(--niko-blue)] group-hover:bg-[var(--niko-gold)] group-hover:text-[var(--niko-dark)] transition-colors">
                  <f.icon className="w-5 h-5" />
                </div>
                <span className="pt-2 text-sm text-[var(--niko-charcoal)]/90">
                  {f.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
